import { useState, useCallback } from 'react'
import { useAuth } from '../providers'
import { GlobalState } from '@highlight/ipc'
import { v1Client, getChatApiUrl } from '../api'

export const useShareConversation = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { getAuthToken } = useAuth()

  const getShareLink = useCallback(async (conversationId: string): Promise<string> => {
    setIsLoading(true)
    setError(null)

    const token = await getAuthToken()

    // TODO: Implement AbortController

    let response

    try {
      response = await v1Client.POST('/conversations/{id}/shares', {
        params: {
          path: {
            id: conversationId,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Share conversation request was aborted')
      } else {
        setError(err instanceof Error ? err.message : 'An unknown error occurred')
      }
      throw err
    } finally {
      setIsLoading(false)
    }

    if (response.error) {
      console.error('Create share link error', response.error)
      const message = typeof response.error === 'string' ? response.error : 'An unknown error occurred'
      setError(message)
      throw new Error(message)
    }

    const sharedConversationId = response.data?.data?.sharedConversationId
    if (!sharedConversationId) {
      const message = 'Share link response missing sharedConversationId'
      console.error(message, response.data)
      setError(message)
      throw new Error(message)
    }

    const chatApiUrl = getChatApiUrl()
    const isStagingBackend = chatApiUrl.includes('staging')
    const env = chatApiUrl.includes('localhost') ? 'env=local' : isStagingBackend ? 'env=staging' : ''

    return `https://highlightai.com/share/${sharedConversationId}${env ? `?${env}` : ''}`
  }, [])

  const getShareLinkMessage = useCallback(async (conversationId: string, messageIds: string[]): Promise<string> => {
    // First, attempt to return a cached share link for this message ID
    const cachedTable = GlobalState.get<Record<string, string>>('message.shareLinks') || {}
    const key = messageIds.join(',')
    if (cachedTable[key]) {
      return cachedTable[key]
    }

    setIsLoading(true)
    setError(null)

    const token = await getAuthToken()

    let response

    try {
      response = await v1Client.POST('/conversations/{id}/shares', {
        params: {
          path: {
            id: conversationId,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          messageIds,
        },
      })
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Share conversation request was aborted')
      } else {
        setError(err instanceof Error ? err.message : 'An unknown error occurred')
      }
      throw err
    } finally {
      setIsLoading(false)
    }

    if (response.error) {
      console.error('Create share link error', response.error)
      const message = typeof response.error === 'string' ? response.error : 'An unknown error occurred'
      setError(message)
      throw new Error(message)
    }

    const sharedConversationId = response.data?.data?.sharedConversationId
    if (!sharedConversationId) {
      const message = 'Share link response missing sharedConversationId'
      console.error(message, response.data)
      setError(message)
      throw new Error(message)
    }

    const chatApiUrl = getChatApiUrl()
    const isStagingBackend = chatApiUrl.includes('staging')
    const env = chatApiUrl?.includes('localhost') ? 'env=local' : isStagingBackend ? 'env=staging' : ''
    const link = `https://highlightai.com/share/${sharedConversationId}${env ? `?${env}` : ''}`

    // Cache the generated link for future look-ups
    GlobalState.set('message.shareLinks', {
      ...cachedTable,
      [key]: link,
    })

    return link
  }, [])

  const getCachedMessageShareLink = useCallback((messageId: string): string | undefined => {
    const table = GlobalState.get<Record<string, string>>('message.shareLinks') || {}
    return table[messageId]
  }, [])

  const deleteShareLink = useCallback(
    async (conversationId: string, messageIds?: string[]): Promise<void> => {
      setIsLoading(true)
      setError(null)

      const token = await getAuthToken()

      let response

      try {
        // For conversation shares, use the DELETE endpoint
        response = await v1Client.DELETE('/conversations/{id}/shares', {
          params: {
            path: {
              id: conversationId,
            },
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          console.log('Delete share link request was aborted')
        } else {
          setError(err instanceof Error ? err.message : 'An unknown error occurred')
        }
        throw err
      } finally {
        setIsLoading(false)
      }

      if (response.error) {
        console.error('Delete share link error', response.error)
        setError(typeof response.error === 'string' ? response.error : 'An unknown error occurred')
        throw new Error(typeof response.error === 'string' ? response.error : 'Failed to delete share link')
      }
      console.log('Delete share link response', response?.data)

      // Clear cached share links
      const cachedTable = GlobalState.get<Record<string, string>>('message.shareLinks') || {}

      if (messageIds) {
        // For message-specific shares, remove the specific cache entry
        const key = messageIds.join(',')
        const updatedCache = { ...cachedTable }
        delete updatedCache[key]
        GlobalState.set('message.shareLinks', updatedCache)
      } else {
        // For conversation shares, remove all share links for this conversation
        const updatedCache = Object.fromEntries(
          Object.entries(cachedTable).filter(([_, link]) => !link.includes(conversationId)),
        )
        GlobalState.set('message.shareLinks', updatedCache)
      }
    },
    [getAuthToken],
  )

  return { getShareLink, getShareLinkMessage, getCachedMessageShareLink, deleteShareLink, isLoading, error }
}
